import { useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { UserAvatar } from "@/components/ui/user-avatar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useDeleteTournamentOrganizer } from "@/graphql/hooks/useDeleteTournamentOrganizer"
import { useToast } from "@/hooks/use-toast"
import { Trash2 } from "lucide-react"

interface TournamentOrganizer {
  id: number
  personId: number
  Person: {
    id: number
    fullName: string
    code: string
    avatar?: string
    urlAvatar?: string
  }
}

interface OrganizerManagementTableProps {
  organizers: TournamentOrganizer[]
  onOrganizerUpdate: () => void
  className?: string
}

export const OrganizerManagementTable = ({
  organizers,
  onOrganizerUpdate,
}: OrganizerManagementTableProps) => {
  const [deleteOrganizerId, setDeleteOrganizerId] = useState<number | null>(null)

  const { deleteTournamentOrganizer, loading: deleteLoading } = useDeleteTournamentOrganizer()
  const { toast } = useToast()

  // Sort organizers alphabetically by name
  const sortedOrganizers = [...organizers].sort((a, b) =>
    a.Person?.fullName.localeCompare(b.Person.fullName),
  )

  const handleDeleteOrganizer = async () => {
    if (!deleteOrganizerId) return

    try {
      await deleteTournamentOrganizer(deleteOrganizerId)
      toast({ title: "Organizador eliminado correctamente", variant: "success" })
      setDeleteOrganizerId(null)
      onOrganizerUpdate()
    } catch (error) {
      toast({
        title: "Error al eliminar organizador",
        description: "No se pudo eliminar el organizador del torneo",
        variant: "destructive",
      })
    }
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>Organizador</TableHead>
            <TableHead>Código</TableHead>
            <TableHead>Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedOrganizers.map((organizer, index) => (
            <TableRow key={organizer.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <UserAvatar
                    src={organizer.Person.urlAvatar || organizer.Person.avatar}
                    name={organizer.Person.fullName}
                    size="sm"
                  />
                  <span className="font-medium">{organizer.Person.fullName}</span>
                </div>
              </TableCell>
              <TableCell>{organizer.Person.code}</TableCell>
              <TableCell>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteOrganizerId(organizer.id)}
                  className="h-8 w-8 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteOrganizerId} onOpenChange={() => setDeleteOrganizerId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar este organizador del torneo? Esta acción no se
              puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOrganizerId(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteOrganizer} disabled={deleteLoading}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
